"use strict";
import { useQuery } from "@tanstack/react-query";
import { BigNumber } from "@ethersproject/bignumber";
import { Contract } from "@ethersproject/contracts";
import { formatUnits } from "@ethersproject/units";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Token } from "@uniswap/sdk-core";
import { useAccountMeta } from "contexts/UniswapContext";
import { useEthersProvider } from "hooks/useEthersProvider";
import { NetworkStatus } from "@apollo/client";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { UniverseChainId } from "features/chains/types";
import { isBscChain, MULTICALL3_ADDRESS } from "constants/bscContracts";
import { getDexTokenList } from "constants/topazTokenList";
import { getCurrencySafetyInfo } from "features/dataApi/utils";
import { getTokenLogoUrl } from "constants/tokenLogos";
import { useTokenLists } from "contexts/TokenListsContext";
import { NativeCurrency } from "features/tokens/NativeCurrency";
import { currencyId } from "utils/uniswapCurrencyId";
import { SafetyLevel, ProtectionResult } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import ERC20_ABI from "abis/erc20.json";
import { Interface } from "@ethersproject/abi";
import { useChainId } from "wagmi";
const MULTICALL3_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "target", type: "address" },
          { internalType: "bool", name: "allowFailure", type: "bool" },
          { internalType: "bytes", name: "callData", type: "bytes" }
        ],
        internalType: "struct Multicall3.Call3[]",
        name: "calls",
        type: "tuple[]"
      }
    ],
    name: "aggregate3",
    outputs: [
      {
        components: [
          { internalType: "bool", name: "success", type: "bool" },
          { internalType: "bytes", name: "returnData", type: "bytes" }
        ],
        internalType: "struct Multicall3.Result[]",
        name: "returnData",
        type: "tuple[]"
      }
    ],
    stateMutability: "payable",
    type: "function"
  }
];
const ERC20_INTERFACE = new Interface(ERC20_ABI);
export function usePortfolioBalances({
  address,
  ...queryOptions
}) {
  const _account = useAccountMeta();
  const { chains } = useEnabledChains();
  const currentChainId = useChainId();
  const userAddedTokens = useSelector((state) => state.user?.tokens || {});
  const { getTokenListTokens, getTokenListLogoUrl } = useTokenLists();
  const primaryChain = chains.find(isBscChain) || UniverseChainId.Mainnet;
  const provider = useEthersProvider({ chainId: primaryChain });
  const isOnCorrectChain = currentChainId === primaryChain;
  const listTokens = getTokenListTokens(primaryChain);
  const { data: balancesData, isLoading: loading, error, refetch } = useQuery({
    queryKey: ["portfolio-balances", address, chains, userAddedTokens, listTokens.length, isOnCorrectChain],
    queryFn: async () => {
      if (!address || !provider || !isOnCorrectChain) return {};
      const allBalances = {};
      try {
        const ethBalance = await provider.getBalance(address);
        if (ethBalance.gt(0)) {
          const nativeCurrency = NativeCurrency.onChain(primaryChain);
          const ethCurrencyId = currencyId(nativeCurrency);
          const safetyInfo = getCurrencySafetyInfo(SafetyLevel.Verified, {
            attackTypes: [],
            result: ProtectionResult.Benign
          });
          const formattedEthBalance = formatUnits(ethBalance, nativeCurrency.decimals);
          const parsedEthBalance = Number.parseFloat(formattedEthBalance);
          allBalances[ethCurrencyId] = {
            id: `eth-balance-${primaryChain}`,
            cacheId: `TokenBalance:eth-balance-${primaryChain}`,
            quantity: Number.isFinite(parsedEthBalance) ? parsedEthBalance : 0,
            quantityRaw: ethBalance.toString(),
            balanceUSD: void 0,
            // We don't have price data for now
            currencyInfo: {
              currencyId: ethCurrencyId,
              currency: nativeCurrency,
              logoUrl: "/images/tokens/bnb-logo.png",
              // BNB logo for native currency
              safetyLevel: SafetyLevel.Verified,
              safetyInfo,
              isSpam: false
            },
            relativeChange24: void 0,
            isHidden: false
          };
        }
        const dexTokens = getDexTokenList(primaryChain);
        const userTokensForChain = userAddedTokens[primaryChain] || {};
        const userTokens = Object.values(userTokensForChain).map(
          (serializedToken) => new Token(
            serializedToken.chainId,
            serializedToken.address,
            serializedToken.decimals,
            serializedToken.symbol,
            serializedToken.name
          )
        );
        const seenAddresses = new Set(dexTokens.map((t) => t.address.toLowerCase()));
        const allTokens = [...dexTokens];
        for (const { token } of listTokens) {
          const key = token.address.toLowerCase();
          if (!seenAddresses.has(key)) {
            seenAddresses.add(key);
            allTokens.push(token);
          }
        }
        const listTokenAddresses = new Set(seenAddresses);
        for (const userToken of userTokens) {
          if (!seenAddresses.has(userToken.address.toLowerCase())) {
            allTokens.push(userToken);
          }
        }
        if (allTokens.length > 0) {
          try {
            const multicall = new Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, provider);
            const calls = allTokens.map((token) => ({
              target: token.address,
              allowFailure: true,
              callData: ERC20_INTERFACE.encodeFunctionData("balanceOf", [address])
            }));
            const results = await multicall.callStatic.aggregate3(calls);
            allTokens.forEach((token, i) => {
              const result = results[i];
              if (!result?.success) return;
              let balance;
              try {
                const [decoded] = ERC20_INTERFACE.decodeFunctionResult("balanceOf", result.returnData);
                balance = BigNumber.from(decoded);
              } catch {
                return;
              }
              if (!balance.gt(0)) return;
              const tokenCurrencyId = currencyId(token);
              const safetyInfo = getCurrencySafetyInfo(SafetyLevel.Verified, {
                attackTypes: [],
                result: ProtectionResult.Benign
              });
              const formattedTokenBalance = formatUnits(balance, token.decimals);
              const parsedTokenBalance = Number.parseFloat(formattedTokenBalance);
              const isListToken = listTokenAddresses.has(token.address.toLowerCase());
              allBalances[tokenCurrencyId] = {
                id: `token-balance-${token.address}-${primaryChain}`,
                cacheId: `TokenBalance:token-balance-${token.address}-${primaryChain}`,
                quantity: Number.isFinite(parsedTokenBalance) ? parsedTokenBalance : 0,
                quantityRaw: balance.toString(),
                balanceUSD: void 0,
                currencyInfo: {
                  currencyId: tokenCurrencyId,
                  currency: token,
                  logoUrl: getTokenLogoUrl(token.address) ?? getTokenListLogoUrl(token.address),
                  safetyLevel: isListToken ? SafetyLevel.Verified : SafetyLevel.MediumWarning,
                  safetyInfo,
                  isSpam: false
                },
                relativeChange24: void 0,
                isHidden: false
              };
            });
          } catch (error2) {
            console.warn("Multicall3 batch balance fetch failed:", error2);
          }
        }
      } catch (error2) {
        console.warn(`Failed to fetch balances for chain ${primaryChain}:`, error2);
      }
      return allBalances;
    },
    enabled: !!address && !queryOptions?.skip && isOnCorrectChain,
    refetchInterval: 3e4
    // Refresh every 30 seconds
  });
  const retry = useCallback(() => {
    return refetch();
  }, [refetch]);
  return {
    data: balancesData,
    loading,
    networkStatus: loading ? NetworkStatus.loading : NetworkStatus.ready,
    refetch: retry,
    error: error ?? void 0
  };
}
export function usePortfolioTotalValue({
  address: _address
}) {
  return {
    data: {
      balanceUSD: 0,
      percentChange: 0,
      absoluteChangeUSD: 0
    },
    loading: false,
    networkStatus: NetworkStatus.ready,
    refetch: () => Promise.resolve({ data: { balanceUSD: 0, percentChange: 0, absoluteChangeUSD: 0 } }),
    error: void 0
  };
}
export function useTokenBalancesGroupedByVisibility({
  balancesById
}) {
  return useMemo(() => {
    if (!balancesById) {
      return { shownTokens: void 0, hiddenTokens: void 0 };
    }
    const balances = Object.values(balancesById);
    return {
      shownTokens: balances.length ? balances : void 0,
      hiddenTokens: void 0
      // No hidden tokens for now
    };
  }, [balancesById]);
}
export function useSortedPortfolioBalances({
  address
}) {
  const {
    data: balancesById,
    loading,
    networkStatus,
    refetch
  } = usePortfolioBalances({ address });
  const { shownTokens } = useTokenBalancesGroupedByVisibility({ balancesById });
  return {
    data: {
      balances: shownTokens || [],
      hiddenBalances: []
    },
    loading,
    networkStatus,
    refetch,
    error: void 0
  };
}
export function useHighestBalanceNativeCurrencyId(_address, chainId) {
  const { defaultChainId } = useEnabledChains();
  const targetChainId = chainId ?? defaultChainId;
  const nativeCurrency = new NativeCurrency(targetChainId);
  return currencyId(nativeCurrency);
}
export function usePortfolioCacheUpdater(_address) {
  return useCallback(() => {
  }, []);
}
export function usePortfolioValueModifiers(_addresses) {
  return void 0;
}
export function sortPortfolioBalances({
  balances,
  isTestnetModeEnabled
}) {
  if (isTestnetModeEnabled) {
    const sortedNativeBalances = balances.filter((b) => b.currencyInfo.currency.isNative).sort((a, b) => (b.quantity ?? 0) - (a.quantity ?? 0));
    const sortedNonNativeBalances = balances.filter((b) => !b.currencyInfo.currency.isNative).sort((a, b) => (a.currencyInfo.currency.symbol || "").localeCompare(b.currencyInfo.currency.symbol || ""));
    return [...sortedNativeBalances, ...sortedNonNativeBalances];
  }
  const balancesWithUSDValue = balances.filter((b) => b.balanceUSD);
  const balancesWithoutUSDValue = balances.filter((b) => !b.balanceUSD);
  return [
    ...balancesWithUSDValue.sort((a, b) => {
      if (!a.balanceUSD) {
        return 1;
      }
      if (!b.balanceUSD) {
        return -1;
      }
      return b.balanceUSD - a.balanceUSD;
    }),
    ...balancesWithoutUSDValue.sort(
      (a, b) => (a.currencyInfo.currency.symbol || "").localeCompare(b.currencyInfo.currency.symbol || "")
    )
  ];
}
